import React, { useCallback } from 'react';
import {
  FaCheck,
  FaExclamationTriangle,
  FaTimes,
  FaTimesCircle,
} from 'react-icons/fa';
import { useSteps } from '../../contexts/stepContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import {
  findingsCountForService,
  ServiceOutcome,
  serviceOutcomeOf,
  severityForCount,
} from '../../utils/services';
import {
  AllServicesTag,
  CloseButton,
  Divider,
  FindingsCount,
  Overlay,
  Panel,
  PanelBody,
  PanelHeader,
  Row,
  RowLabel,
  RowValue,
  ServiceItem,
  ServiceName,
  ServicesBlock,
} from './styles';

interface SummaryProps {
  /** Renderiza como modal sobreposto (usado no mobile). */
  asModal?: boolean;
  onClose?: () => void;
}

/* O ícone acompanha a cor da faixa — a cor sozinha não pode ser o único sinal
   (vermelho x verde é justamente o eixo que quem tem daltonismo não separa). */
const outcomeIcon = (outcome: ServiceOutcome): JSX.Element => {
  if (outcome === 'findings') return <FaTimesCircle size={12} />;
  if (outcome === 'failed') return <FaExclamationTriangle size={12} />;
  return <FaCheck size={12} />;
};

const outcomeTitle = (
  outcome: ServiceOutcome,
  tp: (key: 'outcomeFindings' | 'outcomeFailed' | 'outcomeClean') => string,
): string | undefined => {
  if (outcome === 'findings') return tp('outcomeFindings');
  if (outcome === 'failed') return tp('outcomeFailed');
  if (outcome === 'clean') return tp('outcomeClean');
  return undefined;
};

const Summary: React.FC<SummaryProps> = ({ asModal = false, onClose }) => {
  const {
    currentValidation,
    name,
    documentNum,
    selectedServices,
    contractedServices,
    serviceResults,
    skipPhotoRequested,
    personType,
  } = useSteps();
  const isPJ = personType === 'PJ';
  const { translationPage } = useAppTranslation();
  const { tp } = translationPage(tr => tr.components.summary);

  // Lista vazia é o sentinela de "todos os serviços contratados" — mesmo
  // nesse caso os serviços são listados um a um, com o rótulo do conjunto.
  const allSelected = selectedServices.length === 0;
  const servicesToShow = allSelected ? contractedServices : selectedServices;

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const analysisLabel = (): string => {
    if (isPJ) return tp('analysisTypePj');
    if (currentValidation === 'CNH') return tp('analysisTypeCnh');
    if (currentValidation === 'Foto') return tp('analysisTypeFace');
    if (currentValidation === 'Dados' || skipPhotoRequested)
      return tp('analysisTypeData');
    return tp('empty');
  };

  const panel = (
    <Panel asModal={asModal} onClick={asModal ? stopPropagation : undefined}>
      <PanelHeader>
        <h3>{tp('title')}</h3>
        {asModal && onClose && (
          <CloseButton type="button" onClick={onClose}>
            <FaTimes size={13} />
            {tp('close')}
          </CloseButton>
        )}
      </PanelHeader>
      <PanelBody>
        <Row>
          <RowLabel>{tp('analysisTypeLabel')}</RowLabel>
          <RowValue>{analysisLabel()}</RowValue>
        </Row>
        {/* A consulta PJ não coleta nome — só o CNPJ. */}
        {!isPJ && (
          <Row>
            <RowLabel>{tp('nameLabel')}</RowLabel>
            <RowValue title={name}>{name || tp('empty')}</RowValue>
          </Row>
        )}
        <Row>
          <RowLabel>{tp('documentLabel')}</RowLabel>
          <RowValue mono>{documentNum || tp('empty')}</RowValue>
        </Row>
        <Divider />
        <ServicesBlock>
          <RowLabel>{tp('servicesLabel')}</RowLabel>
          {allSelected && servicesToShow.length > 0 && (
            <AllServicesTag>({tp('allServices')})</AllServicesTag>
          )}
          {servicesToShow.length === 0 ? (
            <ServiceItem>
              <FaCheck size={12} />
              {tp('allServices')}
            </ServiceItem>
          ) : (
            servicesToShow.map(service => {
              const findings = findingsCountForService(serviceResults, service);
              const outcome = serviceOutcomeOf(serviceResults, service);

              return (
                <ServiceItem
                  key={service.cos_ser_id}
                  outcome={outcome}
                  title={outcomeTitle(outcome, tp)}
                >
                  {outcomeIcon(outcome)}
                  <ServiceName>
                    {service.ser_description || service.ser_name}
                  </ServiceName>
                  {findings > 0 && (
                    <FindingsCount
                      severity={severityForCount(findings)}
                      title={tp('findingsCount')}
                    >
                      {findings}
                    </FindingsCount>
                  )}
                </ServiceItem>
              );
            })
          )}
        </ServicesBlock>
      </PanelBody>
    </Panel>
  );

  if (!asModal) return panel;

  return <Overlay onClick={onClose}>{panel}</Overlay>;
};

export default Summary;
